import {
	Dialog,
	FormattedText,
	Toolbar
} from "client/Components";
import { useDataInputDialog, useUnmountedCancellation } from "client/Hooks";
import { clearDmsCache } from "client/Api/Documents/SharePoint/sharepointDocuments";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useCallback,
	useState
} from "react";

export type UnableToConnectToSharePointDialogProps = {
	onConfirmation: () => void;
	onCancellation: () => void;
};

export const UnableToConnectToSharePointDialog = (props: UnableToConnectToSharePointDialogProps): ReactElement => {
	const {
		onConfirmation,
		onCancellation
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);

	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const tryAgainHandler = useCallback(
		async (): Promise<void> => {
			try {
				setIsBusy(true);
				await clearDmsCache(globalCancellation);
				onConfirmation();
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}
				console.error(e);
			}
		},
		[globalCancellation, onConfirmation]
	);

	return (
		<Dialog
			hasBusyIndicator={isBusy}
			trackingId="unable-to-connect-to-sharePointDialog-dialog"
		>
			<Dialog.Header
				title={localize("_UnableToConnectoToSharePointDialogTitle")}
				onClose={onCancellation}
			/>
			<Dialog.Body>
				<FormattedText
					text={localize("_UnableToConnectoToSharePointDialogMessage")}
					thingsToReplace={[]}
				/>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize("_TryAgain")}
						onClick={tryAgainHandler}
					/>
					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onCancellation}
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
UnableToConnectToSharePointDialog.displayName="UnableToConnectToSharePointDialog";

export type ConfirmationDialogInitialData = Omit<
	UnableToConnectToSharePointDialogProps,
	"onConfirmation" | "onCancellation"
>;

export function useUnableToConnectToSharePointDialog(): [
	element: ReactElement | null,
	open: (props: ConfirmationDialogInitialData) => Promise<TryResult<void>>
] {
	return useDataInputDialog(
		(props, onCancellation, onConfirmation) => (
			<UnableToConnectToSharePointDialog
				{...props}
				onConfirmation={onConfirmation}
				onCancellation={onCancellation}
			/>
		)
	);
}